import React, {useRef, useEffect} from 'react';
import {Animated, StyleSheet, View, Easing} from 'react-native';
import {SkillzText} from '@skillz/ui';
import ThemeOverrideProps from 'ThemeOverrideProps';
import SkillzImage from 'SkillzImage';
import LinearGradient from 'react-native-linear-gradient';
import {_m} from 'INTL';

const {fontSemiBold, fontExtraBold} = ThemeOverrideProps.sheetStyles;

interface RewardItemProps {
  reward: number;
  delay: number;
  type: string;
  finishedCallback: (name: string) => void;
}

const RewardItem = ({reward, delay, finishedCallback, type}: RewardItemProps) => {
  // Anim values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const translateY = useRef(new Animated.Value(0)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;
  const starScaleAnim = useRef(new Animated.Value(1)).current;

  const rewardIcon =
    type === 'bonusCash'
      ? 'icon_fancy_cash.png'
      : type === 'ticketz'
        ? 'icon_deposit_reward_ticketz.png'
        : 'icon_deposit_reward_z_coins.png';

  // NOTE: Start centered on X
  const translateX = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.sequence([
      // Phase 1: Fade in only (centered horizontally, offset down 100px, big)
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 100,
        delay,
        useNativeDriver: true,
      }),

      // Phase 2: Slide diagonally + shrink to normal scale
      Animated.parallel([
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: 100,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(translateY, {
          toValue: 0,
          duration: 100,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(translateX, {
          toValue: 0, // keeps centered at the end too
          duration: 100,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    ]).start(({finished}) => {
      if (finished) {
        finishedCallback(type);
      }
    });

    Animated.loop(
      Animated.sequence([
        Animated.timing(starScaleAnim, {
          toValue: 1,
          duration: 300, // spin duration
          useNativeDriver: true,
        }),
        Animated.delay(1000), // ⏸ pause for 1s
        Animated.timing(starScaleAnim, {
          toValue: 0,
          duration: 800, // spin duration
          useNativeDriver: true,
        }),
      ])
    ).start();

    Animated.loop(
      Animated.sequence([
        Animated.timing(rotateAnim, {
          toValue: 1,
          duration: 3000, // spin duration
          useNativeDriver: true,
        }),
        Animated.delay(1000), // ⏸ pause for 1s
        Animated.timing(rotateAnim, {
          toValue: 0, // reset back
          duration: 0, // instant reset
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [fadeAnim, scaleAnim, translateY, translateX, delay, rotateAnim]);

  // Map 0 → 1 to 0deg → 360deg
  const spin = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  const shiningStarPosition =
    type === 'bonusCash'
      ? {left: 26, top: -32}
      : type === 'ticketz'
        ? {left: 25, top: -32}
        : {left: 15, top: -30};

  const getRewardText = () => {
    if (reward >= 1000 && type !== 'bonusCash') {
      const value = reward / 1000;
      return value % 1 === 0 ? `${value}K` : `${value.toFixed(1)}K`;
    }
    return reward.toString();
  };

  const getRewardTextFontSize = () => {
    const rewardText = getRewardText();
    const rewardTextLength = rewardText.length;
    if (rewardTextLength > 5) {
      return 20;
    } else return 22;
  };

  const rewardText =
    type === 'bonusCash' ? _m('Bonus Cash') : type === 'ticketz' ? _m('Ticketz') : _m('Z Coins');

  return (
    <Animated.View
      style={[
        {
          opacity: fadeAnim,
          transform: [{scale: scaleAnim}, {translateY}, {translateX}],
        },
      ]}
    >
      <LinearGradient
        colors={['#666666', '#000000']}
        start={{x: 0.5, y: 0}}
        end={{x: 0.5, y: 1}}
        style={styles.borderContainer}
      >
        <View style={styles.contentContainer}>
          <View style={styles.rewardIconContainer}>
            <SkillzImage source={rewardIcon} style={styles.rewardIcon} />
            <View style={[{...shiningStarPosition, ...styles.shiningStar}]}>
              <Animated.View style={[{transform: [{scale: starScaleAnim}, {rotate: spin}]}]}>
                <SkillzImage source='icon_shining_star_white.png' style={styles.shiningStar} />
              </Animated.View>
            </View>
          </View>
          <View style={styles.rewardAmountContainer}>
            <SkillzText style={[styles.rewardText, {fontSize: getRewardTextFontSize()}]}>
              {type === 'bonusCash' ? '$' : ''}
              {getRewardText()}
            </SkillzText>
            <SkillzText style={styles.bonusCashText}>{rewardText}</SkillzText>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  borderContainer: {
    width: 152,
    height: 52,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    width: 149,
    height: 50,
    borderRadius: 30,
  },

  rewardIconContainer: {
    width: 28,
    height: 32,
  },

  rewardIcon: {
    width: 35,
    height: '100%',
  },

  shiningStar: {
    width: 10,
    height: 10,
  },

  rewardAmountContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: -5,
  },

  rewardText: {
    ...fontExtraBold,
    fontSize: 22,
    color: '#FFFFFF',
  },

  bonusCashText: {
    ...fontSemiBold,
    fontSize: 13,
    color: '#FFFFFF',
  },
});

export default RewardItem;
